import { useApolloClient } from '@apollo/client';
import { captureException, close } from '@sentry/react';
import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AUTH_SIGN_IN, AUTH_SIGN_OUT, ERROR_TRACE } from '../constants/localState';
import { UserRole } from '../graphql/generated/schema';
import { AppRoutes } from '../pages/AppRoutes';
import { useIsPublic } from './useIsPublic.hook';

export interface AuthResponse {
  role: Lowercase<UserRole>;
  id: string;
  exp: number;
}

export interface JwtRefreshToken {
  /** Unique identifier of the `User` */
  id: string;
  aud: string;
  iat: number;
  exp: number;
  iss: string;
}
export interface JwtAccessToken extends JwtRefreshToken {
  role: Lowercase<UserRole>;
}

export interface SignInDto {
  username: string;
  password: string;
}

export interface AuthCookie {
  signInCookie: string | undefined;
}

export type Auth = [
  AuthCookie | undefined,
  {
    signIn: (signInDto: SignInDto) => Promise<AuthResponse | undefined>;
    signOut: () => Promise<void>;
    reSignIn: (signInDto: SignInDto) => Promise<void>;
    refresh: () => Promise<AuthResponse | undefined>;
    // SendPasswordResetEmail: (email: string) => void;
    // confirmPasswordReset: (code: string, password: string) => void;
  },
];

const AUTO_REFRESH_TARGET = 10_000; // Milliseconds before end of life of the current token
const AUTO_REFRESH_OFFSET = 5_000; // Milliseconds margin/ offset from the target
const HTTP_ERROR_500 = 500;
const HTTP_EMPTY_BODY = 204;

// Internal context provided by <ProvideAuth /> and accessed via useAuth() hook
const AuthContext = createContext<Auth>([undefined, {}] as Auth);

// Hook for child components to get the auth object
// and re-render when it changes.
export const useAuth = (): Auth => {
  return useContext(AuthContext);
};

/**
 * External function overwritten by the useProvideAuth function from the `useAuth` hook
 */
export let refreshAuth = async (): Promise<AuthResponse | undefined> => {
  // eslint-disable-next-line no-console
  console.error('refreshAuth() called before it was set');
  return;
};

/**
 * External function overwritten by the useProvideAuth function from the `useAuth` hook
 */
export let setSignedOut = async (): Promise<void> => {
  // eslint-disable-next-line no-console
  console.error('refreshFailed() called before it was set');
};

export let isPublic: boolean | (() => void) = () => {
  // eslint-disable-next-line no-console
  console.error('isPublic called before it was set');
  return;
};

export let authCookieRef: AuthCookie | undefined;

let isSigningOut = false;

// Provider hook that creates auth object and handles state
function useProvideAuth(): Auth {
  const { t } = useTranslation(['auth', 'translation']);

  const [authCookie, setAuthCookie] = useState<AuthCookie | undefined>(undefined);
  const [refreshTimer, setRefreshTimer] = useState<NodeJS.Timeout>();

  const client = useApolloClient();

  // Helper function to parse and decode a jwt
  const responseToRefreshTimer = useCallback(
    ({ exp }: AuthResponse) => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      // Refresh 10 seconds before the access token expires (with a minimum of 5 seconds)
      const offset = exp - new Date().getTime() - AUTO_REFRESH_TARGET;
      setRefreshTimer(setTimeout(() => refreshAuth(), offset > AUTO_REFRESH_OFFSET ? offset : AUTO_REFRESH_OFFSET));
    },
    [refreshTimer],
  );

  isPublic = useIsPublic();

  // Overwrite refreshAuth function, it can still be called from outside of the hook
  refreshAuth = useCallback(async () => {
    // Skip refresh if we are on the ErrorPage
    if (window.location.pathname === AppRoutes.Error) return;

    // Attempt to refresh the access token by using the Refresh token (stored in a cookie)
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
    });

    // If the response is empty (httpStatus === 204), the refresh token is invalid and we should sign out
    if (response.status === HTTP_EMPTY_BODY) {
      setSignedOut();
      return;
    }
    if (!response.ok) {
      let error = '';
      if (response.status >= HTTP_ERROR_500) {
        error = await response.text();

        if (error) {
          captureException(error);
          sessionStorage.setItem(ERROR_TRACE, JSON.stringify(error));
        }

        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        close(2000).then(function () {
          // eslint-disable-next-line require-atomic-updates
          window.location.href = AppRoutes.Error;
        });
      }
      try {
        const errorObj = JSON.parse(error);
        if (errorObj.message === 'Unauthorized' && !isPublic) {
          // Longer duration since the refresh is a background task and not a direct user action
          toast.error(t('auth:account-invalidated'), { duration: 10_000 });
        }
      } catch (e) {
        if (!isPublic) {
          // Longer duration since the refresh is a background task and not a direct user action
          toast.error(t('auth:account-invalidated'), { duration: 10_000 });
        } else {
          toast.error(t('translation:messages.error.unknown-error'), { duration: 10_000 });
        }
      }
      // Do not show an error if (error.message === 'No refresh token found'), this is always the case on first load
      setSignedOut();
      return;
    }
    const responseData: AuthResponse = await response.json();
    responseToRefreshTimer(responseData);
    setAuthCookie({ signInCookie: document.cookie });

    return responseData;
  }, [responseToRefreshTimer, t]);

  // Overwrite setSignedOut function. Function can also be called from outside the context (in createApolloClient)
  setSignedOut = useCallback(async () => {
    if (refreshTimer) clearTimeout(refreshTimer);

    // Reset the Apollo store (which clears the persisted cache)
    await client.resetStore();

    // Announce that the user is singed out, other tabs/ windows on the same device will listen to this event
    window.localStorage.setItem(AUTH_SIGN_OUT, Date.now().toString());

    setAuthCookie({ signInCookie: undefined });
  }, [client, refreshTimer]);

  const signIn: Auth[1]['signIn'] = useCallback(
    async (signInDto) => {
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(signInDto),
      });
      if (!response.ok) {
        if (response.status >= HTTP_ERROR_500) {
          const error = await response.text();

          if (error) {
            captureException(error);
            sessionStorage.setItem(ERROR_TRACE, JSON.stringify(error));
          }

          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          close(2000).then(function () {
            // eslint-disable-next-line require-atomic-updates
            window.location.href = AppRoutes.Error;
          });
        }
        const error = await response.json();
        if (error.message === 'Unauthorized') {
          toast.error(t('translation:messages.error.unknown-credentials'));
        } else {
          toast.error(t('translation:messages.error.unknown-error'));
        }
        return;
      }
      const responseData: AuthResponse = await response.json();
      responseToRefreshTimer(responseData);

      setAuthCookie({ signInCookie: document.cookie });
      window.localStorage.setItem(AUTH_SIGN_IN, Date.now().toString());
      return responseData;
    },
    [t, responseToRefreshTimer],
  );

  const signOut: Auth[1]['signOut'] = useCallback(async () => {
    if (isSigningOut) return;
    isSigningOut = true;
    await fetch('/api/auth/sign-out', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
    });
    // eslint-disable-next-line require-atomic-updates
    isSigningOut = false;
    await setSignedOut();
    toast.success(t('auth:signed-out'));
  }, [t]);

  const reSignIn: Auth[1]['reSignIn'] = useCallback(
    async (signInDto) => {
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(signInDto),
      });
      const responseData: AuthResponse = await response.json();
      responseToRefreshTimer(responseData);
      setAuthCookie({ signInCookie: document.cookie });
      toast.success(t('auth:re-signed-in'));
    },
    [t, responseToRefreshTimer],
  );

  // Listen to localStorage, if the AUTH_SIGN_OUT is updated, we should sign this window out as well
  useEffect(() => {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === AUTH_SIGN_OUT) {
        toast.success(t('auth:signed-out'));
        setAuthCookie({ signInCookie: undefined });
      } else if (event.key === AUTH_SIGN_IN) {
        toast.success(t('auth:signed-in'));
        window.location.reload();
      }
    });
  }, [t]);

  useEffect(() => {
    authCookieRef = authCookie;
  }, [authCookie]);

  // Refresh authentication on mount (if the useAuth hook is used)
  useEffect(() => {
    refreshAuth();
  }, []);

  return [
    authCookie,
    {
      signIn,
      signOut,
      reSignIn,
      refresh: refreshAuth,
    },
  ];
}

// Provider component that wraps your app and makes auth object
// available to any child component that calls useAuth().
export const ProvideAuth: FC<PropsWithChildren> = ({ children }) => {
  const auth = useProvideAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
